import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

import { usersQueryOptions } from '~/services/users';
import { Route as PortalRoute } from '~/routes/_authed/portal';
import { AppLink } from '~/components/layouts/AuthedLayout/AuthedLayout';
import { PractitionerModal } from '~/components/PractitionerModal/PractitionerModal';
import { Practitioner } from '@repo/types';
import { UsersList } from '~/components/UsersList/UsersList';
import { TextLink, Typography } from '@repo/ui';

export const Route = createFileRoute('/_authed/users')({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(usersQueryOptions()),
  component: Users,
});

function Users() {
  const { data: users } = useSuspenseQuery(usersQueryOptions());
  const usersQty =
    (users?.practitioners?.length ?? 0) + (users?.admin_user ? 1 : 0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] =
    useState<Practitioner | null>(null);

  const addPractitioner = () => {
    setSelectedPractitioner(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (userId: string) => {
    const practitioner = users?.practitioners?.find(p => p.id === userId);
    if (practitioner) {
      setSelectedPractitioner(practitioner);
      setIsModalOpen(true);
    }
  };

  return (
    <div className='flex flex-column gap-2 overflow-auto flex-grow-1 pr-2'>
      <TextLink
        label='Back to Portal'
        to={PortalRoute.to}
        LinkComponent={AppLink}
        preload='intent'
        replace
        iconNameLeft='chevronBack'
      />
      <Typography variant='heading-m'>{`Users & permissions`}</Typography>
      <div className='flex align-items-center justify-content-between gap-1'>
        <Typography
          variant='body-s'
          className='dark-grey'
        >{`${usersQty} user${usersQty > 1 ? 's' : ''}`}</Typography>
        <TextLink
          label='Add practitioner'
          color='purple'
          iconNameLeft='add'
          onClick={() => addPractitioner()}
        />
      </div>
      <div className='flex flex-column gap-2'>
        <UsersList users={users} onEditUser={handleEditUser} />
      </div>
      <PractitionerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        practitioner={selectedPractitioner}
      />
    </div>
  );
}
